import {
  get,
  isArray,
  forEach,
  isEmpty,
  at,
  map,
  some
} from 'lodash'
import Bus from '../../bus'
import { logger } from '../../utils'
import { CmsApi } from '../../api'

const COMPONENT_NO_OPTIONS = {}

class SiteConfigManager {
  constructor () {
    this.config = {}
    this.navigation = {}
    this.widgets = {}
    this.extra = {}
    this.layout = {}
    this.siteConfigLoaded = false
  }

  getConfig (configurationKey, defaultValue) {
    return get(
      this.config,
      configurationKey,
      defaultValue
    )
  }

  getExtraConfig (configurationKey, defaultValue) {
    return get(
      this.extra,
      configurationKey,
      defaultValue
    )
  }

  getComponentPath (componentType, defaultComponentPath) {
    return this.getExtraConfig(
      `pathList.${componentType}`,
      defaultComponentPath
    )
  }

  getComponentOptions (componentType) {
    return this.getExtraConfig(
      `componentOptions.${componentType}`,
      COMPONENT_NO_OPTIONS
    )
  }

  getSubComponentOptions (componentType, subComponentType) {
    return this.getExtraConfig(
      `componentOptions[${componentType}/${subComponentType}]`,
      COMPONENT_NO_OPTIONS
    )
  }

  /*
    Initially configured before application initialized
  */
  setLayoutConfig (layout) {
    this.layout = layout
  }

  setConfig (config) {
    this.config = config
  }

  setExtraConfig (extra) {
    this.extra = extra
  }

  async resolveCmsConfig () {
    const ac = new AbortController()
    const siteConfigDefer = CmsApi.getSiteSettings({ signal: ac.signal })
      .then((data) => {
        this.setConfig(data)

        // FIXME: Temp solution
        this.setExtraConfig(
          __parseLayoutConfig(
            this.config.config,
            this.layout
          )
        )
      })
      .catch(error => {
        logger.error('[siteconfig]', 'unable to load cms settings')
        throw error
      })

    const navigationDefer = CmsApi.getNavigation({ signal: ac.signal })
      .then((navigation) => {
        this.navigation = navigation
      })
      .catch(error => {
        logger.error('[siteconfig]', 'unable to load cms navigation')
        throw error
      })

    const widgetsDefer = CmsApi.getWidgets({ signal: ac.signal })
      .then((widgets) => {
        this.widgets = widgets
      })
      .catch(error => {
        logger.error('[siteconfig]', 'unable to load cms widgets')
        throw error
      })

    return Promise.all([
      siteConfigDefer,
      navigationDefer,
      widgetsDefer
    ])
      .then(payload => {
        Bus.emit(Bus.events.config.resolved, payload)
      })
      .catch(error => {
        Bus.emit(Bus.events.config.unresolved, {
          error
        })
        ac.abort()
      })
  }
}

const siteConfigManager = new SiteConfigManager()

export default siteConfigManager

// Internal private helpers

function __parseLayoutConfig (siteConfig, components) {
  const layout = isArray(siteConfig)
    ? siteConfig
    : get(siteConfig, 'pages', [])

  // register components in system
  const processedComponents = []
  const componentOptions = {}
  // create an object where key is type of component and value is path to it
  const pathList = {}

  // merge data about components which came from BE and data which we pass to this function
  forEach(layout, layoutItem => {
    let [type, isActive, options, tabs] = at(
      layoutItem,
      ['type', 'isActive', 'options', 'options.tabs']
    )
    const component = get(components, type)

    if (!component) {
      return
    }

    // Processing nested pages stored as tabs
    if (tabs && tabs.length) {
      tabs = map(tabs, tab => {
        const [isActive, subTabs] = at(tab, ['isActive', 'tabs'])
        // if has sub tabs, should have at leas 1 active sub tab
        if (isEmpty(subTabs)) {
          return {
            ...tab,
            // to be active should has active sub tabs (if has one's)
            isActive
          }
        }

        // Storing tabs options with combined indexes
        // Second nesting level
        const nestedComponentIndex = `${type}/${tab.type}`
        componentOptions[nestedComponentIndex] = {
          tabs: tab.tabs
        }

        return {
          ...tab,
          // to be active should has active sub tabs (if has one's)
          isActive: some(subTabs, 'isActive') && isActive
        }
      })
    }

    // if has tabs, should have at leas 1 active tab
    const hasActiveTabs = isEmpty(tabs) ? true : some(tabs, 'isActive')

    const processedComponentOptions = {
      ...options,
      tabs
    }

    const processedComponent = {
      ...component,
      ...layoutItem,
      // to be active should has active tabs (if has one's)
      isActive: hasActiveTabs && isActive,
      options: processedComponentOptions
    }

    // Indexing component options by component type
    componentOptions[processedComponent.type] = processedComponentOptions

    pathList[processedComponent.type] = (processedComponent.path)
      ? processedComponent.path
      : processedComponent.defaultPath

    processedComponents.push(processedComponent)
  })

  return {
    components: processedComponents,
    componentOptions,
    pathList
  }
}
